import { useState } from "react";
import { toast } from "sonner";
import { Pencil, RefreshCw, Save, X, ArrowUpDown } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { Textarea } from "../ui/textarea";
import { SectionOrder } from "./SectionOrder";
import { PriceFill } from "./PriceFill";

const arr = (x) => (Array.isArray(x) ? x : x ? [x] : []);
const Ps = ({ list }) => arr(list).map((p, i) => <p key={i}>{p}</p>);

const SectionView = ({ s }) => (
  <div className="space-y-3 text-stone-600 text-[15px] leading-relaxed">
    {s.intro && <p className="font-display italic text-xl leading-relaxed text-[#2B2A26]/85">{s.intro}</p>}
    <Ps list={s.paragraphs} />
    {(s.subsections || []).map((x, i) => (
      <div key={i}><div className="font-display text-lg text-[#9A7B2C] mt-2">{x.heading}</div><Ps list={x.paragraphs} />
        {x.bullets?.length > 0 && <ul className="list-disc pl-5">{x.bullets.map((b, j) => <li key={j}>{b}</li>)}</ul>}</div>
    ))}
    {s.for_you && <ul className="list-disc pl-5">{[...s.for_you, ...(s.not_for || []).map((x) => `Not for: ${x}`)].map((b, i) => <li key={i}>{b}</li>)}</ul>}
    {s.table?.columns?.length > 0 && (
      <div className="overflow-auto"><table className="w-full text-xs border-collapse">
        <thead><tr>{s.table.columns.map((c) => <th key={c} className="text-left p-2 bg-stone-100 text-[#9A7B2C] font-mono-ui">{c}</th>)}</tr></thead>
        <tbody>{(s.table.rows || []).map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className="p-2 border-b border-stone-200 align-top">{c}</td>)}</tr>)}</tbody>
      </table></div>
    )}
    {[...(s.cards || []), ...(s.points || []), ...(s.categories || []).map((c) => ({ title: `${c.name} — ${c.amount}`, text: c.desc })),
      ...(s.steps || []).map((c) => ({ title: `${c.title} · ${c.timing}`, text: c.text })), ...(s.numbers || []).map((c) => ({ title: c.number, text: c.label })),
      ...(s.contacts || []).map((c) => ({ title: c.name, text: c.detail })), ...(s.blocks || []).map((c) => ({ title: c.when, text: arr(c.items).join(" · ") })),
      ...(s.columns || []).map((c) => ({ title: c.heading, text: arr(c.items).join(" · ") }))].map((c, i) => (
      <div key={i} className="text-xs"><b className="text-[#2B2A26]">{c.title}</b> — {c.text}</div>
    ))}
    {(s.entries || []).map((e, i) => (
      <div key={i} className="border-l-2 border-[#C9A847]/50 pl-4 py-1">
        <div className="font-display text-lg text-[#2B2A26]">{e.name} <span className="text-xs text-stone-500 font-sans">{e.location}</span></div>
        <p className="mt-1">{arr(e.paragraphs || e.description || e.lead || e.subtitle)[0]}</p>
        {e.signature && <p className="text-[#9A7B2C] text-xs mt-1">Ask for: {e.signature}</p>}
        <div className="text-xs text-stone-500 mt-1">{[e.price, e.hours, e.phone, e.website].filter(Boolean).join(" · ")}</div>
        {(e.costs || []).length > 0 && (
          <div data-testid={`entry-costs-${i}`} className="mt-1 text-xs text-stone-600">
            <b className="text-[#9A7B2C]">What it costs:</b>{" "}
            {e.costs.map((c, j) => (
              <span key={j}>{j > 0 && " · "}{c.item}{c.amount ? ` ${c.amount}` : (
                <span data-testid={`entry-cost-unverified-${i}-${j}`} className="ml-1 rounded-sm bg-amber-100 px-1 text-amber-800">price not verified</span>
              )}</span>
            ))}
          </div>
        )}
      </div>
    ))}
    {(s.days || []).map((d) => (
      <div key={d.day} className="border-l-2 border-[#3B82F6]/50 pl-4 py-1">
        <div className="font-display text-lg text-[#2B2A26]">Day {d.day} — {d.title}</div>
        {["morning", "afternoon", "evening"].map((k) => <p key={k}><b className="text-stone-500 capitalize">{k}</b> {arr(d[k])[0]}</p>)}
      </div>
    ))}
    {[["Tip", s.tip], ["Note", s.warn], ["Callout", s.callout]].filter(([, v]) => v).map(([l, v]) => (
      <p key={l} className="text-xs text-[#9A7B2C]">{l}: {typeof v === "object" ? `${v.title ? v.title + " — " : ""}${v.text || ""}` : v}</p>
    ))}
  </div>
);

const roman = (i) => {
  let n = i + 1, out = "";
  [[50, "L"], [40, "XL"], [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"]].forEach(([v, s]) => { while (n >= v) { out += s; n -= v; } });
  return out;
};
const clean = (x) => (x || "").replace(/\*/g, "");
const iconBtn = "p-2 border border-white/30 text-white/80 hover:text-white hover:bg-white/10 transition-colors";

const SectionCard = ({ kit, s, i, c, reload }) => {
  const [edit, setEdit] = useState(false);
  const [text, setText] = useState("");
  const open = () => { setText(JSON.stringify(s, null, 2)); setEdit(true); };
  const save = async () => {
    try {
      await http.put(`/kits/${kit.id}/sections/${s.key}`, { section: JSON.parse(text) });
      toast.success("Section saved");
      setEdit(false);
      reload();
    } catch (e) { toast.error(e instanceof SyntaxError ? "Invalid JSON" : errMsg(e)); }
  };
  const regen = async () => {
    try {
      await http.post(`/kits/${kit.id}/regenerate`, { group: s.key });
      toast.success(`Regenerating “${clean(s.title)}”`);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const lead = s.stand || (s.kind === "entries" ? `${(s.entries || []).length} places, each in full` : s.toc);
  return (
    <article data-testid={`section-card-${s.key}`}>
      <div className="relative px-8 py-9" style={{ background: c.dark }}>
        <div className="absolute right-4 top-4 flex gap-2">
          {s.kind !== "how_to_book" && <button data-testid={`section-regenerate-${s.key}`} onClick={regen} title="Regenerate this chapter" className={iconBtn}><RefreshCw size={14} /></button>}
          {!edit && <button data-testid={`section-edit-${s.key}`} onClick={open} title="Edit" className={iconBtn}><Pencil size={14} /></button>}
        </div>
        <div className="font-display text-4xl" style={{ color: c.border }}>{roman(i)}</div>
        <h3 className="font-display text-3xl sm:text-4xl text-white mt-2">{clean(s.title || s.essay_title)}</h3>
        {lead && <p className="font-display italic text-white/75 mt-3 max-w-2xl">{lead}</p>}
      </div>
      {edit ? (
        <div className="mt-6">
          <Textarea data-testid={`section-editor-${s.key}`} value={text} onChange={(e) => setText(e.target.value)} className="font-mono-ui text-xs min-h-[360px] bg-[#F7F5F0]" />
          <div className="flex gap-2 mt-3">
            <button data-testid={`section-save-${s.key}`} onClick={save} className="inline-flex items-center gap-1.5 rounded-sm bg-[#C9A847] text-white px-4 py-1.5 text-sm font-bold hover:bg-[#B8973A]"><Save size={14} /> Save</button>
            <button data-testid={`section-cancel-${s.key}`} onClick={() => setEdit(false)} className="inline-flex items-center gap-1.5 rounded-sm border border-stone-300 px-4 py-1.5 text-sm text-stone-600 hover:text-stone-900"><X size={14} /> Cancel</button>
          </div>
        </div>
      ) : <div className="mt-8 chapter-body"><SectionView s={s} /></div>}
    </article>
  );
};

const ChapterList = ({ kit, sel, setSel, reload }) => {
  const [reorder, setReorder] = useState(false);
  return (
    <nav className="lg:sticky lg:top-6 self-start">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-stone-400">{kit.sections.length} chapters</div>
        <button data-testid="toggle-reorder" onClick={() => setReorder(!reorder)} className="text-xs text-stone-500 hover:text-[#2B2A26] inline-flex items-center gap-1"><ArrowUpDown size={12} /> {reorder ? "Done" : "Reorder"}</button>
      </div>
      {reorder ? <SectionOrder kit={kit} reload={reload} /> : (
        <ol className="max-h-[70vh] overflow-y-auto">
          {kit.sections.map((s, i) => (
            <li key={s.key}>
              <button data-testid={`chapter-link-${s.key}`} onClick={() => setSel(i)}
                className={`w-full text-left flex gap-4 px-3 py-2 text-sm transition-colors ${i === sel ? "bg-[#2B2A26] text-white" : "text-[#2B2A26] hover:bg-stone-100"}`}>
                <span className={`w-8 shrink-0 font-display ${i === sel ? "text-white/60" : "text-stone-400"}`}>{roman(i)}.</span>{clean(s.title || s.essay_title)}
              </button>
            </li>
          ))}
        </ol>
      )}
    </nav>
  );
};

export const ContentTab = ({ kit, reload, c }) => {
  const [sel, setSel] = useState(0);
  const i = Math.min(sel, kit.sections.length - 1);
  return (
    <>
    {kit.collection === "researched" && <PriceFill kitId={kit.id} reload={reload} />}
    <div className="grid lg:grid-cols-[240px_1fr] gap-8 xl:gap-10">
      <ChapterList kit={kit} sel={i} setSel={setSel} reload={reload} />
      <div className="max-w-3xl min-w-0">
        <SectionCard key={kit.sections[i].key} kit={kit} s={kit.sections[i]} i={i} c={c} reload={reload} />
      </div>
    </div>
    </>
  );
};
